import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MessageCircle, Image as ImageIcon, Sparkles, X } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { waLink } from "../constants/data";
import { Sparkle, Heart, Gift, Flower } from "../components/doodles/Doodles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const msg = (name) =>
  `Hi Naathi Charami! 👋 I'd love to enquire about your "${name}" — please share catalogues, options & pricing for my event. Thank you!`;

export default function Catalogues() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/catalogues`)
      .then((r) => {
        const found = (r.data || []).find((c) => c.slug === "previous-works");
        setData(found || null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    if (lightbox !== null) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [lightbox]);

  if (loading) {
    return <div className="pt-32 pb-20 text-center font-hand text-[#5C5555]">Loading…</div>;
  }

  const images = ((data?.images) || []).filter(Boolean).slice(0, 30);
  const wa = waLink(msg(data?.name || "Previous Works"));

  return (
    <div className="pt-28 pb-20 min-h-[80vh] relative overflow-hidden" data-testid="catalogues-page">
      <Helmet>
        <title>Previous Works & Bulk Order Catalogues — Naathi Charami</title>
        <meta name="description" content={(data?.description || "Browse our previous works — return gifts, corporate, weddings, baby showers and more.").slice(0, 160)}/>
      </Helmet>

      <div className="absolute inset-0 -z-10" style={{
        background: "radial-gradient(circle at 20% 20%, #FFE0B5 0%, transparent 45%), radial-gradient(circle at 80% 80%, #FFD4DC 0%, transparent 45%), linear-gradient(135deg, #FFF7E6 0%, #FFE7F1 100%)",
      }}/>

      <Sparkle color="#FFD93D" className="absolute top-32 left-10 w-6 animate-twinkle"/>
      <Sparkle color="#FF6B9D" className="absolute top-40 right-20 w-5 animate-twinkle" style={{ animationDelay: "0.5s" }}/>
      <Heart color="#FF6B9D" className="absolute bottom-32 right-12 w-7 animate-bounce-doodle"/>
      <Gift color="#DDC5FF" className="absolute bottom-20 left-12 w-9 animate-float-slow"/>
      <Flower color="#FFA94D" className="absolute -top-3 -left-3 w-24 rotate-12 opacity-80"/>
      <Flower color="#FF6B9D" className="absolute -bottom-4 -right-4 w-24 -rotate-12 opacity-80"/>

      <div className="container-x relative">
        <Link to="/" data-testid="catalogues-back-link" className="inline-flex items-center gap-1.5 font-hand text-sm text-[#7A1F2B] hover:underline mb-4">
          <ArrowLeft className="w-4 h-4"/> Back to home
        </Link>

        <div className="text-center max-w-2xl mx-auto">
          <p className="label-small text-xs sm:text-sm">📦 Bulk Orders &amp; Event Gifting 📦</p>
          <h1 className="font-doodle text-3xl sm:text-4xl lg:text-5xl text-[#7A1F2B] mt-2 leading-tight" data-testid="catalogues-name">
            Our previous <span className="text-[#FF6B9D]">works</span>
          </h1>
          {data?.description ? (
            <p className="mt-3 font-hand text-base text-[#2D2424] leading-relaxed whitespace-pre-line" data-testid="catalogues-description">
              {data.description}
            </p>
          ) : (
            <p className="font-hand text-sm sm:text-base text-[#5C5555] mt-2 italic">
              Browse our recent bulk orders — return gifts, corporate, weddings & more.
            </p>
          )}
        </div>

        {/* WhatsApp CTA */}
        <div className="mt-5 text-center">
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            data-testid="catalogues-whatsapp-btn"
            className="btn-whatsapp inline-flex"
          >
            <MessageCircle className="w-4 h-4"/> Enquire on WhatsApp
          </a>
        </div>

        {/* Important notes */}
        {data?.important_notes && (
          <div className="mt-6 max-w-2xl mx-auto px-3 py-2 bg-[#FFF7DC] border-2 border-dashed border-[#7A1F2B]/40 rounded-xl">
            <p className="text-[10px] font-doodle uppercase tracking-widest text-[#7A1F2B] flex items-center gap-1.5">
              <Sparkles className="w-3 h-3"/> Important
            </p>
            <p className="font-hand text-xs text-[#2D2424] mt-1 whitespace-pre-line">{data.important_notes}</p>
          </div>
        )}

        {/* Image gallery */}
        <section className="mt-10" data-testid="catalogues-gallery">
          <h2 className="font-doodle text-2xl text-[#7A1F2B] mb-3 flex items-center gap-2">
            <ImageIcon className="w-5 h-5"/> Previous works
          </h2>
          {images.length === 0 ? (
            <div className="aspect-[4/3] max-w-2xl mx-auto bg-[#FFE7F1] border-2 border-dashed border-[#7A1F2B]/40 rounded-3xl flex flex-col items-center justify-center gap-2 text-[#9B9494]">
              <ImageIcon className="w-10 h-10 opacity-50"/>
              <p className="font-hand text-sm">Images coming soon</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="catalogues-image-grid">
              {images.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightbox(i)}
                  data-testid={`catalogues-image-${i}`}
                  className="aspect-square doodle-card overflow-hidden bg-[#FFE7F1] hover:-translate-y-0.5 transition-transform"
                  style={{ boxShadow: "4px 4px 0 #2D2424" }}
                >
                  <img src={src} alt={`Previous work ${i + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" loading="lazy"/>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <p className="font-hand text-sm text-[#5C5555] mb-3 italic">Looking for something similar?</p>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            data-testid="catalogues-whatsapp-bottom"
            className="btn-whatsapp inline-flex"
          >
            <MessageCircle className="w-4 h-4"/> Chat with us on WhatsApp
          </a>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightbox !== null && images[lightbox] && (
        <div
          className="fixed inset-0 z-[100] bg-[#2D2424]/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}
          data-testid="catalogues-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white border-2 border-[#2D2424] flex items-center justify-center"
            style={{ boxShadow: "3px 3px 0 #2D2424" }}
            aria-label="close"
          >
            <X className="w-5 h-5"/>
          </button>
          <img src={images[lightbox]} alt="" className="max-w-full max-h-[88vh] object-contain rounded-2xl"/>
        </div>
      )}
    </div>
  );
}
